import React from 'react';
import { Table, Text, Loading } from '@nextui-org/react';
import { AdminSessionList, AdminSessionItem } from '../../types/sessions';

interface SessionListProps {
    data: AdminSessionList | null;
    isLoading: boolean;
    error?: string | null;
    onSelectSession: (id: string) => void;
    selectedSessionId: string | null;
}

const SessionList: React.FC<SessionListProps> = ({ data, isLoading, error, onSelectSession, selectedSessionId }) => {
    if (isLoading && !data) {
        return <Loading />;
    }

    if (error) {
        return <Text color="error">{error}</Text>;
    }

    if (!data || data.items.length === 0) {
        return <Text>No sessions found.</Text>;
    }

    const columns = [
        { name: "STARTED AT", uid: "started_at" },
        { name: "PARTICIPANT", uid: "participant_id" },
        { name: "COHORT", uid: "cohort" },
        { name: "CONDITION", uid: "condition" },
        { name: "DURATION", uid: "duration" },
        { name: "STATUS", uid: "status" },
        { name: "FINAL STAGE", uid: "final_stage" },
        { name: "TURNS", uid: "turns" },
        { name: "RECORDING", uid: "recording" },
        { name: "PROMPT VERSION", uid: "prompt_version" }
    ];

    const renderCell = (item: AdminSessionItem, columnKey: React.Key) => {
        switch (columnKey) {
            case "started_at": {
                 if (!item.started_at) return <Text>-</Text>;
                 const d = new Date(item.started_at);
                 const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                 return <Text size={12} title={d.toISOString()}>{formatted}</Text>;
            }
            case "participant_id":
                 return <Text size={12} title={item.participant_id || ''}>{item.participant_id ? item.participant_id.substring(0, 8) : '-'}</Text>;
            case "cohort":
                 return <Text size={12}>{item.cohort || '-'}</Text>;
            case "condition": {
                 let color = '$text';
                 if (item.condition === 'socratic_only') color = '$neutral';
                 if (item.condition === 'hybrid') color = '$primary';
                 if (item.condition === 'mentor_only') color = '#9b59b6'; // amethyst
                 return <Text size={12} css={{ color }}>{item.condition || '-'}</Text>;
            }
            case "duration": {
                if (item.completion_status === 'in_progress' || !item.duration_ms) {
                    return <Text size={12}>in progress</Text>;
                }
                const totalSeconds = Math.floor(item.duration_ms / 1000);
                const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
                const ss = String(totalSeconds % 60).padStart(2, '0');
                return <Text size={12}>{mm}:{ss}</Text>;
            }
            case "status":
                 return <Text size={12}>{item.completion_status || '-'}</Text>;
            case "final_stage":
                 return <Text size={12}>{item.final_stage || '-'}</Text>;
            case "turns":
                 return <Text size={12}>{item.turn_count}</Text>;
            case "recording":
                 return <Text size={12}>{item.has_recording ? '🎥' : '-'}</Text>; // Neutral icon
            case "prompt_version":
                 return <Text size={12}>{item.prompt_version || '-'}</Text>;
            default:
                 return null;
        }
    };

    return (
        <Table
            aria-label="Sessions list"
            selectionMode="single"
            color="primary"
            onSelectionChange={(keys) => {
                if (keys !== "all") {
                    const selectedId = Array.from(keys)[0] as string;
                    if (selectedId) {
                        onSelectSession(selectedId);
                    }
                }
            }}
            selectedKeys={selectedSessionId ? new Set([selectedSessionId]) : new Set()}
        >
             <Table.Header columns={columns}>
                {(column) => (
                    <Table.Column key={column.uid}>{column.name}</Table.Column>
                )}
             </Table.Header>
             <Table.Body items={data.items}>
                 {(item) => (
                     <Table.Row key={item.session_id}>
                         {(columnKey: React.Key) => <Table.Cell>{renderCell(item, columnKey)}</Table.Cell>}
                     </Table.Row>
                 )}
             </Table.Body>
             <Table.Pagination
                  shadow
                  noMargin
                  align="center"
                  rowsPerPage={data.page_size}
                  total={Math.ceil(data.total / data.page_size)}
             />
        </Table>
    );
};

export default SessionList;